import React from 'react'
import { Alert, AlertTitle, List, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { PairedRuuviTag } from '../../types'
import RuuviTagScannedListItemButton from './RuuviTagScannedListItemButton'

const EMPTY_EXCLUDED_RUUVI_TAG_IDS: string[] = []

export interface RuuviTagScannerListProps {
  ruuviTags: PairedRuuviTag[]
  excludedRuuviTagIds?: string[]
  onSelect: (tag: PairedRuuviTag) => void
  onExitInstallation: () => void
  onTagUpdate?: (tag: PairedRuuviTag) => void
}

export default function RuuviTagScannerList({
  ruuviTags,
  excludedRuuviTagIds,
  onSelect,
  onExitInstallation,
  onTagUpdate
}: RuuviTagScannerListProps) {
  const { t } = useTranslation('installation')
  const stableExcludedRuuviTagIds = excludedRuuviTagIds ?? EMPTY_EXCLUDED_RUUVI_TAG_IDS

  const visibleRuuviTags = React.useMemo(() => {
    if (stableExcludedRuuviTagIds.length === 0) {
      return ruuviTags
    }

    const excludedRuuviTagIdSet = new Set(stableExcludedRuuviTagIds)

    return ruuviTags.filter((tag) => !excludedRuuviTagIdSet.has(tag.id))
  }, [ruuviTags, stableExcludedRuuviTagIds])

  if (!visibleRuuviTags?.length) return null

  return (
    <Stack direction="column" gap={2} sx={{ mt: 2 }}>
      <Alert color="success">
        <AlertTitle sx={{ m: 0 }}>
          {visibleRuuviTags.length > 1
            ? t('foundRuuviTags', { count: visibleRuuviTags.length })
            : t('foundRuuviTag')}
        </AlertTitle>
      </Alert>

      <List disablePadding>
        {visibleRuuviTags.map((tag) => (
          <RuuviTagScannedListItemButton
            key={tag.id}
            tag={tag}
            onSelect={onSelect}
            onExitInstallation={onExitInstallation}
            onTagUpdate={onTagUpdate}
          />
        ))}
      </List>
    </Stack>
  )
}
